// Privacy mode (task 402.10, ADR-066 §5, owner decision M3): a per-browser display preference that shortens
// personal data on screen, for sharing a screen or working in public. Persisted like the sidebar collapse.
//
// It is NOT a control. The server has already decided what this operator may see (task 402.5); this only changes
// how the values they were given are drawn, and toggling it off shows them again without a refetch.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** One row of the api's classification vocabulary (api/console/config/sensitive-fields, task 402.10). */
export interface SensitiveField {
  name: string;
  sensitivity: 'PII' | 'SECRET' | string;
  mask: string;
}

export interface SensitiveFieldCatalog {
  fields: SensitiveField[];
  formats: string[];
  countryFieldHint: string;
}

interface PrivacyState {
  enabled: boolean;
  /**
   * The api's vocabulary, loaded once by the shell. Undefined until it arrives, or for good if the call failed —
   * masking falls back to its own list then, which covers more rather than less.
   */
  catalog?: SensitiveFieldCatalog;
  toggle: () => void;
  setEnabled: (enabled: boolean) => void;
  setCatalog: (catalog: SensitiveFieldCatalog) => void;
}

export const usePrivacyStore = create<PrivacyState>()(
  persist(
    (set, get) => ({
      enabled: false,
      toggle: () => set({ enabled: !get().enabled }),
      setEnabled: (enabled) => set({ enabled }),
      setCatalog: (catalog) => set({ catalog }),
    }),
    {
      name: 'bancorix:privacy',
      // Only the preference is remembered; the vocabulary is fetched fresh, so a classification change on the
      // server is never masked by a stale browser copy.
      partialize: (state) => ({ enabled: state.enabled }),
    },
  ),
);

/** Reads the flag without subscribing — for helpers called outside React. */
export const isPrivacyMode = (): boolean => usePrivacyStore.getState().enabled;
